import { Box, useMediaQuery, useTheme } from '@mui/material';
import { useState, useEffect, ReactNode } from 'react';
import { Navigation } from './Navigation';
import { Sidebar } from './Sidebar';
import { useDescriptionMappingStore } from '../store/descriptionMappingStore';

interface AppLayoutProps {
  children: ReactNode;
}

const DRAWER_STATE_KEY = 'sidebarOpen';

export const AppLayout = ({ children }: AppLayoutProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const fetchMappings = useDescriptionMappingStore((state) => state.fetchMappings);

  useEffect(() => {
    void fetchMappings();
  }, [fetchMappings]);

  // Persistent sidebar state via localStorage
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    const saved = localStorage.getItem(DRAWER_STATE_KEY);
    return saved !== null ? JSON.parse(saved) : !isMobile;
  });

  // Update localStorage when sidebar state changes
  useEffect(() => {
    localStorage.setItem(DRAWER_STATE_KEY, JSON.stringify(sidebarOpen));
  }, [sidebarOpen]);

  // Auto-close on mobile, auto-open on desktop
  useEffect(() => {
    setSidebarOpen(!isMobile);
  }, [isMobile]);

  const handleSidebarToggle = () => {
    // Only allow toggling on mobile
    if (isMobile) {
      setSidebarOpen(!sidebarOpen);
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* AppBar */}
      <Navigation onMenuClick={handleSidebarToggle} showMenuButton={true} />

      {/* Sidebar */}
      <Sidebar
        open={sidebarOpen}
        onClose={handleSidebarToggle}
        variant={isMobile ? 'temporary' : 'permanent'}
      />

      {/* Main content area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: 'background.default',
          mt: '64px', // AppBar height
          minHeight: 'calc(100vh - 64px)',
          minWidth: 0, // Allow flex item to shrink below content size
          overflowX: 'hidden', // Prevent horizontal scroll at layout level
        }}
      >
        {children}
      </Box>
    </Box>
  );
};
